import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { money, shortDate } from "@/lib/api";
import { ArrowLeft, TrendingUp, TrendingDown, Target as TargetIcon, Package, AlertCircle } from "lucide-react";

export function AccountsList() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/customers").then(r => setCustomers(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="accounts-list-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Account Performance</h1>
        <p className="text-slate-600 mt-1">Choose a store to view full performance breakdown</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Store</th>
              <th className="text-left px-4 py-3">Group</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Agent</th>
              <th className="text-left px-4 py-3">Last Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`account-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-slate-600">{c.customer_type || "—"}</td>
                <td className="px-4 py-3">{c.state}</td>
                <td className="px-4 py-3 text-slate-600">{c.assigned_agent_name || "—"}</td>
                <td className="px-4 py-3 text-xs">{shortDate(c.last_order_date) || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/accounts/${c.id}`} className="text-[#003DA5] font-semibold hover:underline" data-testid={`view-account-${c.id}`}>View →</Link>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">No accounts.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AccountPerformance() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.get(`/customers/${cid}/account-performance`).then(r => setData(r.data)).catch(() => nav("/accounts"));
    api.get("/sales-opportunities", { params: { customer_id: cid } }).then(r => setOpps(r.data)).catch(() => {});
  }, [cid, nav]);

  if (!data) return <div className="text-slate-500">Loading account…</div>;
  const c = data.customer; const s = data.sales; const t = data.target;
  const growth = s.growth_pct;

  return (
    <div className="space-y-6" data-testid="account-performance-page">
      <Link to="/accounts" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#003DA5]">
        <ArrowLeft className="w-4 h-4" /> All accounts
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{c.account_number}</div>
            <h1 className="text-3xl font-heading font-black text-[#001F54]">{c.name}</h1>
            <div className="text-slate-600 mt-1">{c.store_name} • {c.town}, {c.state}</div>
            <div className="text-xs text-slate-500 mt-2">Group: {c.customer_group || "—"} • Assigned agent: <span className="font-semibold text-[#003DA5]">{c.assigned_agent_name || "Unassigned"}</span></div>
          </div>
          <Link to={`/customers/${c.id}`} className="text-sm text-[#003DA5] font-semibold hover:underline self-start">Open store profile →</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Gross Sales (Mo)" value={money(s.gross_this_month)} testid="kpi-gross" />
        <Kpi label="Credit Notes (Mo)" value={money(s.credits_this_month)} tone="red" testid="kpi-credits" />
        <Kpi label="Net Sales (Mo)" value={money(s.net_this_month)} tone="blue" testid="kpi-net" />
        <Kpi label="YTD Sales" value={money(s.ytd)} testid="kpi-ytd" />
        <Kpi label="Previous Year Total" value={money(s.prev_year)} testid="kpi-prev-year" />
        <Kpi
          label="YoY Growth"
          value={growth == null ? "—" : `${growth >= 0 ? "+" : ""}${growth}%`}
          tone={growth == null ? "neutral" : growth >= 0 ? "green" : "red"}
          icon={growth >= 0 ? TrendingUp : TrendingDown}
          testid="kpi-yoy"
        />
        <Kpi label="Last Order" value={shortDate(data.last_order_date) || "—"} testid="kpi-last-order" />
        <Kpi label="Days Since Order" value={data.days_since_last_order != null ? data.days_since_last_order : "—"} testid="kpi-days-since" />
      </div>

      {t && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="target-block">
          <div className="flex items-center gap-2 mb-3"><TargetIcon className="w-5 h-5 text-[#003DA5]" /><h3 className="text-lg font-heading font-semibold">{t.target_name}</h3></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Target" value={money(t.target_value)} testid="tgt-value" />
            <Kpi label="Achieved (Store MTD)" value={money(t.achieved)} tone="blue" testid="tgt-achieved" />
            <Kpi label="Remaining" value={money(t.remaining)} testid="tgt-remaining" />
            <Kpi label="% of Target" value={`${t.achievement_pct}%`} tone={t.achievement_pct >= 100 ? "green" : t.achievement_pct >= 60 ? "blue" : "red"} testid="tgt-pct" />
          </div>
          <div className="mt-4 h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${t.achievement_pct >= 100 ? "bg-emerald-500" : "bg-[#003DA5]"}`} style={{ width: `${Math.min(t.achievement_pct, 100)}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Top Products Purchased" icon={Package}>
          {data.top_products.length === 0 ? <Empty text="No purchases yet." /> : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.top_products.map((p, i) => (
                <li key={i} className="py-2 flex justify-between" data-testid={`top-prod-${i}`}>
                  <span>{p.name}</span>
                  <span className="font-semibold">{money(p.value)} <span className="text-xs text-slate-500">({p.quantity} units)</span></span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Products Not Purchased" icon={AlertCircle}>
          {data.products_not_purchased.length === 0 ? <div className="text-sm text-emerald-600">Great — this store has bought every catalogue product.</div> : (
            <ul className="text-sm space-y-1 max-h-56 overflow-y-auto">
              {data.products_not_purchased.map(p => (
                <li key={p.id} className="py-1 flex items-center justify-between border-b last:border-0 border-slate-100" data-testid={`not-purch-${p.id}`}>
                  <span>{p.name}</span>
                  <span className="text-xs text-slate-500">{p.category}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recent Visits">
          {data.recent_visits.length === 0 ? <Empty text="No visits recorded." /> : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.recent_visits.slice(0, 6).map(v => (
                <li key={v.id} className="py-2">
                  <div className="flex justify-between"><span className="font-medium">{shortDate(v.visit_date)}</span><span className="text-xs text-slate-500">{v.agent_name}</span></div>
                  <div className="text-xs text-slate-600">{v.visit_purpose || "Visit recorded"}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Opportunities for this Store">
          {opps.length === 0 ? <div className="text-sm text-slate-500">No open opportunities.</div> : (
            <ul className="text-sm space-y-2 max-h-56 overflow-y-auto">
              {opps.slice(0, 8).map((o, i) => (
                <li key={i} className={`p-2 rounded border ${o.severity === "high" ? "bg-red-50 border-red-200" : o.severity === "medium" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid={`opp-${i}`}>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-600">{o.type.replace(/_/g, " ")}</div>
                  <div>{o.message}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, tone = "default", icon: Icon, testid }) => {
  const c = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-600" : tone === "blue" ? "text-[#003DA5]" : tone === "neutral" ? "text-slate-500" : "text-[#001F54]";
  return (
    <div className="bg-[#F3F7FC] rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className={`text-lg font-heading font-bold mt-0.5 flex items-center gap-1 ${c}`}>{Icon && <Icon className="w-4 h-4" />}{value}</div>
    </div>
  );
};
const Section = ({ title, children, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-3">{Icon && <Icon className="w-4 h-4 text-[#003DA5]" />}<h3 className="text-lg font-heading font-semibold">{title}</h3></div>
    {children}
  </div>
);
const Empty = ({ text }) => <div className="text-sm text-slate-500">{text}</div>;
